/*
 * The team's own campaigns in the click-through preview: made-up clients run through the same rules the server uses
 * (@steward/shared custom-campaigns). Nothing is sent.
 */
import {
  audienceFits,
  customBody,
  customDefaults,
  customProblems,
  customTemplate,
  CUSTOM_TEMPLATES,
  type Audience,
  type CustomConfig,
  type CustomCount,
  type CustomPage,
  type CustomRow,
  type Facts,
} from "@steward/shared";

type Answer = Promise<{ status: number; body: unknown }>;
const ok = (body: unknown): Answer => Promise.resolve({ status: 200, body });
const fail = (status: number, code: string, message: string): Answer => Promise.resolve({ status, body: { code, message } });

const DAY = 86_400_000;
const ago = (days: number) => new Date(Date.now() - days * DAY).toISOString().slice(0, 10);

/** Name, last visit days ago, kind of work, tags, ZIP, city, plan now, paid dollars, source, has email. */
const MADE_UP = [
  ["Sarah Lee", 40, "Home Clean", ["VIP"], "70005", "Metairie", true, 2400, "Google", true],
  ["James Ortiz", 220, "Deep Cleaning", [], "70001", "Metairie", false, 380, "Facebook", true],
  ["Mia Chen", 400, "Move Out Cleaning", [], "70115", "New Orleans", false, 290, "Google", false],
  ["Theo Grant", 210, "Home Clean", ["Pets"], "70005", "Metairie", false, 900, "Referral", true],
  ["Nora Price", 15, "Home Clean", [], "70118", "New Orleans", true, 3100, "Google", true],
  ["Leah Brooks", 190, "Window Cleaning", [], "70002", "Metairie", false, 260, "Yelp", true],
  ["Omar Haddad", 95, "Deep Cleaning", ["Pets"], "70119", "New Orleans", false, 520, "Google", true],
  ["Ruth Kim", 300, "Home Clean", [], "70005", "Metairie", false, 150, "Door hanger", false],
  ["Dana White", 60, "Window Cleaning", ["VIP"], "70001", "Metairie", true, 1800, "Referral", true],
  ["Carl Young", 500, "Deep Cleaning", [], "70115", "New Orleans", false, 210, "Facebook", true],
] as const;

const FACTS: { name: string; f: Facts }[] = MADE_UP.map(([name, days, svc, tags, zip, city, plan, paid, source, email]) => ({
  name,
  f: {
    tags,
    services: new Map([[svc as string, ago(days)]]),
    planNow: plan,
    planEver: plan,
    lastVisit: ago(days),
    nextVisit: plan,
    visits: plan ? 12 : 1,
    paidCents: paid * 100,
    zips: [zip],
    cities: [city],
    since: ago(days + 30),
    source,
    business: false,
    prefers: email ? "email" : "text",
    mobile: true,
    email,
  },
}));

const all = new Map<string, Map<string, CustomConfig>>();
const mine = (companyId: string) => {
  let m = all.get(companyId);
  if (!m) all.set(companyId, (m = new Map<string, CustomConfig>()));
  return m;
};

function countOf(a: Audience): CustomCount {
  const d = new Date().toISOString().slice(0, 10);
  const list = FACTS.filter((x) => audienceFits(a, x.f, d));
  return {
    people: list.length,
    sms: list.filter((x) => x.f.mobile).length,
    email: list.filter((x) => x.f.email).length,
    names: list.map((x) => ({ name: x.name, sms: x.f.mobile, email: x.f.email })),
  };
}

function page(key: string, c: CustomConfig, business: string): CustomPage {
  const words = (get: (f: Facts) => readonly string[]) => [...new Set(FACTS.flatMap((x) => get(x.f)))].sort();
  return {
    key,
    name: c.name,
    on: c.on,
    start: c.start,
    date: c.date,
    since: c.since,
    audience: c.audience,
    steps: c.steps,
    count: countOf(c.audience),
    people: { going: 0, done: 0, stopped: 0 },
    stats: {},
    problems: customProblems(c),
    pickers: {
      tags: words((f) => f.tags),
      services: words((f) => [...f.services.keys()]),
      sources: words((f) => [f.source]),
      cities: words((f) => f.cities),
    },
    business,
    optout: "Reply STOP to opt out.",
  };
}

export function customRowsDemo(companyId: string): CustomRow[] {
  return [...mine(companyId)].map(([key, c]) => ({
    key,
    name: c.name || "New campaign",
    what: `${String(c.steps.length)} ${c.steps.length === 1 ? "step" : "steps"}`,
    on: c.on,
    line: "",
  }));
}

/** marketing/campaigns/custom... routes; null when the path is not one of them. */
export function customRoute(companyId: string, business: string, method: string, r: string, b: unknown): Answer | null {
  const m = mine(companyId);
  const body = (b ?? {}) as Record<string, unknown>;
  if (method === "POST" && r === "-count") return ok(countOf((body.audience ?? { match: "all", rules: [] }) as Audience));
  if (method === "POST" && r === "") {
    const t = CUSTOM_TEMPLATES.find((x) => x === body.template) ?? "blank";
    const key = `c_${Math.random().toString(36).slice(2, 10).padEnd(8, "0")}`;
    const c = { ...customDefaults(), ...customTemplate(t) };
    m.set(key, c);
    return ok(page(key, c, business));
  }
  const key = r.slice(1);
  const c = m.get(key);
  if (!r.startsWith("/") || !c) return null;
  if (method === "DELETE") {
    m.delete(key);
    return null;
  }
  if (method === "PUT") {
    const parsed = customBody.safeParse(b);
    if (!parsed.success) return fail(400, "bad_request", "That didn't save. Check the steps and try again.");
    const next = { ...c, ...parsed.data };
    const problems = customProblems(next);
    if (next.on && problems.length) return fail(422, "campaign_incomplete", problems[0]!);
    if (next.on && !c.on) next.since = Date.now();
    m.set(key, next);
    return ok(page(key, next, business));
  }
  return ok(page(key, c, business));
}
